import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { ApiError, CreateFolderRequest, Folder, UpdateFolderRequest } from '../types/api';
import { useCreateFolder, useDeleteFolder, useUpdateFolder } from './useFolders';
import { useDialog } from '../components/DialogProvider';

/**
 * State for managing the create/edit modal
 */
interface ModalState {
  isOpen: boolean;
  mode: 'create' | 'edit';
  folder?: Folder;
}

/**
 * State for managing loading states
 */
interface LoadingState {
  action: string | null;
}

/**
 * Return type for useFolderManager hook
 */
interface UseFolderManagerReturn {
  loading: LoadingState;
  error: string | null;
  modal: ModalState;
  openCreateModal: () => void;
  openEditModal: (folder: Folder) => void;
  closeModal: () => void;
  handleCreateFolder: (name: string) => Promise<void>;
  handleUpdateFolder: (name: string) => Promise<void>;
  handleDeleteFolder: (folder: Folder) => Promise<void>;
}

/**
 * Custom hook for managing folder operations and state
 */
export function useFolderManager(): UseFolderManagerReturn {
  const [loading, setLoading] = useState<LoadingState>({
    action: null
  });
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState>({
    isOpen: false,
    mode: 'create'
  });

  // React Query mutations
  const createFolderMutation = useCreateFolder();
  const updateFolderMutation = useUpdateFolder();
  const deleteFolderMutation = useDeleteFolder();

  /**
   * Handle folder creation
   */
  const handleCreateFolder = async (name: string) => {
    try {
      setLoading(prev => ({ ...prev, action: 'create' }));
      setError(null);

      const request: CreateFolderRequest = { name };
      await createFolderMutation.mutateAsync(request);
      
      // The React Query mutation will automatically update the cache
      // and trigger re-renders in components using useFolders
      
      closeModal();
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Failed to create folder');
    } finally {
      setLoading(prev => ({ ...prev, action: null }));
    }
  };

  /**
   * Handle folder update
   */
  const handleUpdateFolder = async (name: string) => {
    if (!modal.folder) return;

    const folderId = modal.folder.id;

    try {
      setLoading(prev => ({ ...prev, action: `update-${folderId}` }));
      setError(null);

      const request: UpdateFolderRequest = { name };
      await updateFolderMutation.mutateAsync({ id: folderId, data: request });
      
      // The React Query mutation will automatically update the cache
      // and trigger re-renders in components using useFolders
      
      closeModal();
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Failed to update folder');
    } finally {
      setLoading(prev => ({ ...prev, action: null }));
    }
  };

  /**
   * Handle folder deletion with confirmation
   */
  const handleDeleteFolder = async (folder: Folder) => {
    const confirmMessage = folder.flowCount > 0 
      ? `Are you sure you want to delete "${folder.name}"? This folder contains ${folder.flowCount} workflow(s) that will be moved to "No folder".`
      : `Are you sure you want to delete "${folder.name}"?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setLoading(prev => ({ ...prev, action: `delete-${folder.id}` }));
      await deleteFolderMutation.mutateAsync(folder.id);
      
      // The React Query mutation will automatically update the cache
      // and trigger re-renders in components using useFolders
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Failed to delete folder');
    } finally {
      setLoading(prev => ({ ...prev, action: null }));
    }
  };

  /**
   * Open modal for creating a new folder
   */
  const openCreateModal = () => {
    setModal({ isOpen: true, mode: 'create' });
    setError(null);
  };

  /**
   * Open modal for editing an existing folder
   */
  const openEditModal = (folder: Folder) => {
    setModal({ isOpen: true, mode: 'edit', folder });
    setError(null);
  };

  /**
   * Close the modal and reset form
   */
  const closeModal = () => {
    setModal({ isOpen: false, mode: 'create' });
    setError(null);
  };

  return {
    loading,
    error,
    modal,
    openCreateModal,
    openEditModal,
    closeModal,
    handleCreateFolder,
    handleUpdateFolder,
    handleDeleteFolder,
  };
} 